"use client";

import { bisector } from "d3-array";
import { scaleBand, scaleLinear, scaleTime } from "d3-scale";
import type { Transition } from "motion/react";
import {
  Children,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useModuleRevealGate } from "@/components/motion/module-reveal-gate";
import { useViewerLanguage } from "@/lib/i18n";
import { DEFAULT_ANIMATION_EASING } from "./animation";
import { ProjectionFadeOut } from "./bar-projection-context";
import {
  isClipExcludedComponent,
  isPostOverlayComponent,
  isUnderlayComponent,
} from "./chart-child-passthrough";
import {
  type ChartContextValue,
  ChartProvider,
  type LineConfig,
  type Margin,
  type TooltipData,
} from "./chart-context";
import { isGradientDefComponent, isPatternDefComponent } from "./chart-defs";
import { shortDateFmt } from "./chart-formatters";
import { type ChartPhase, DEFAULT_CHART_LIFECYCLE } from "./chart-phase";
import {
  cameraFromPose,
  ChartProjectionProvider,
  depthOf,
  PROJECTION_POSES,
  type ProjectionMode,
} from "./projection";
import { extractReferenceAreaConfigs } from "./reference-area-config";
import {
  type ScatterProjectionLayout,
  ScatterProjectionProvider,
  type ScatterProjectionValue,
  scatterFloorSlot,
} from "./scatter-projection-context";
import {
  type ScatterInteractionMode,
  useScatterChartInteraction,
} from "./use-scatter-chart-interaction";
import {
  bandCenterOffset,
  formatXLabel,
  type XRawValue,
  type XScaleKind,
} from "./x-scale";
import {
  buildYScalesForLines,
  getPrimaryYScale,
  normalizeYAxisId,
} from "./y-axis-scales";

export interface ScatterChartInnerProps {
  width: number;
  height: number;
  data: Record<string, unknown>[];
  xDataKey: string;
  margin: Margin;
  animationDuration: number;
  animationEasing?: string;
  enterTransition?: Transition;
  revealSignature?: string;
  children: ReactNode;
  containerRef: React.RefObject<HTMLDivElement | null>;
  lines: LineConfig[];
  xScaleKind?: XScaleKind;
  onPhaseChange?: (phase: ChartPhase) => void;
  projection?: ProjectionMode;
  projectionDepth?: number;
  projectionLayout?: ScatterProjectionLayout;
  interaction?: ScatterInteractionMode;
  xDomain?: [number, number];
  yDomain?: [number, number];
}

const PROJECTION_PAD = 32;
const MAX_DEPTH_SHARE = 0.5;
const MAX_LANE_SHARE = 0.25;
const PILLAR_EXTENT_SHARE = 0.55;
const PILLAR_EXTENT_MIN = 24;
const PILLAR_FOOTPRINT_SHARE = 0.024;
const PILLAR_FOOTPRINT_MIN = 6;
const FLOOR_ISO_CAMERA = cameraFromPose(PROJECTION_POSES.quadrant.iso);

export function ScatterChartInner({
  width,
  height,
  data,
  xDataKey,
  margin,
  animationDuration,
  animationEasing = DEFAULT_ANIMATION_EASING,
  enterTransition,
  revealSignature = "",
  children,
  containerRef,
  lines,
  xScaleKind = "time",
  onPhaseChange,
  projection,
  projectionDepth,
  projectionLayout = "lanes",
  interaction = "x",
  xDomain,
  yDomain,
}: ScatterChartInnerProps) {
  const revealGate = useModuleRevealGate();
  const holdReveal = revealGate === "held";
  const skipReveal = revealGate === "done";
  /**
   * Read through a ref, never as an effect dependency: the page marks itself
   * revealed while this point reveal is still running, and a dependency would
   * drop the settle timer mid-flight — the points would never reach
   * `isLoaded`.
   */
  const skipRevealRef = useRef(skipReveal);
  skipRevealRef.current = skipReveal;
  const [isLoaded, setIsLoaded] = useState(skipReveal);
  const [revealEpoch, setRevealEpoch] = useState(0);

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const xAccessor = useCallback(
    (d: Record<string, unknown>): XRawValue => {
      const value = d[xDataKey];
      if (value instanceof Date || typeof value === "number" || typeof value === "string") {
        return value;
      }
      return xScaleKind === "time"
        ? new Date(value as string | number)
        : (value as XRawValue);
    },
    [xDataKey, xScaleKind]
  );

  const bisectX = useMemo(() => {
    if (xScaleKind === "band") return null;
    return bisector<Record<string, unknown>, number>((d) => Number(xAccessor(d))).left;
  }, [xAccessor, xScaleKind]);

  const xRangePadding = useMemo(() => {
    if (lines.length === 0) {
      return 12;
    }
    const maxRadius = Math.max(...lines.map((line) => line.strokeWidth ?? 5));
    return maxRadius + 10;
  }, [lines]);

  const xScale = useMemo(() => {
    const rangeStart = xRangePadding;
    const rangeEnd = Math.max(xRangePadding, innerWidth - xRangePadding);

    if (xScaleKind === "band") {
      const domain = data.map((d) => String(xAccessor(d)));
      return scaleBand<string>()
        .range([rangeStart, rangeEnd])
        .domain(domain)
        .padding(0.1);
    }

    const values = data.map((d) => Number(xAccessor(d)));
    const minX = xDomain
      ? xDomain[0]
      : values.length > 0
        ? Math.min(...values)
        : 0;
    const maxX = xDomain
      ? xDomain[1]
      : values.length > 0
        ? Math.max(...values)
        : minX;

    if (xScaleKind === "linear") {
      return scaleLinear<number>()
        .range([rangeStart, rangeEnd])
        .domain([minX, maxX]);
    }

    return scaleTime<number>()
      .range([rangeStart, rangeEnd])
      .domain([minX, maxX]);
  }, [innerWidth, data, xAccessor, xRangePadding, xScaleKind, xDomain]);

  const columnWidth = useMemo(() => {
    if (data.length < 2) {
      return 0;
    }
    return innerWidth / (data.length - 1);
  }, [innerWidth, data.length]);

  const yScales = useMemo(
    () =>
      buildYScalesForLines({
        lines,
        data,
        innerHeight,
        resolveDomain: (dataKeys) => {
          if (yDomain) {
            return yDomain;
          }
          let maxValue = 0;
          for (const d of data) {
            for (const key of dataKeys) {
              const value = d[key];
              if (typeof value === "number" && value > maxValue) {
                maxValue = value;
              }
            }
          }
          const top = maxValue <= 0 ? 100 : maxValue * 1.1;
          return [0, top];
        },
      }),
    [innerHeight, data, lines, yDomain]
  );

  const yScale = getPrimaryYScale(
    yScales,
    scaleLinear<number>().range([innerHeight, 0]).domain([0, 100])
  );

  const { intlLocale } = useViewerLanguage();
  const dateLabels = useMemo(
    () =>
      data.map((d) => {
        const raw = xAccessor(d);
        if (xScaleKind === "time" && raw instanceof Date) {
          return shortDateFmt(intlLocale).format(raw);
        }
        return formatXLabel(raw, xScaleKind, intlLocale);
      }),
    [data, xAccessor, xScaleKind, intlLocale]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: revealSignature
  useEffect(() => {
    // The page this chart sits on is not in view yet — the point reveal waits
    // instead of playing to an audience that cannot see it.
    if (holdReveal) {
      return;
    }
    if (skipRevealRef.current) {
      setIsLoaded(true);
      return;
    }
    setRevealEpoch((n) => n + 1);
    setIsLoaded(false);
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, animationDuration);
    return () => clearTimeout(timer);
  }, [animationDuration, holdReveal, revealSignature]);

  useEffect(() => {
    onPhaseChange?.(isLoaded ? "ready" : "revealing");
  }, [isLoaded, onPhaseChange]);

  const hasProjection = projection !== undefined;
  const isIsometric = projection === "isometric";
  const [projectionSettled, setProjectionSettled] = useState(true);
  const canInteract = hasProjection
    ? isLoaded && projectionSettled
    : isLoaded;
  const interactionMode: ScatterInteractionMode = isIsometric
    ? "point"
    : interaction;

  const {
    tooltipData,
    setTooltipData,
    selection,
    clearSelection,
    pointEnter,
    pointLeave,
    pointTap,
    resetPointState,
    interactionHandlers,
    interactionStyle,
  } = useScatterChartInteraction({
    xScale,
    xScaleKind,
    yScale: yScale as ChartContextValue["yScale"],
    yScales: yScales as ChartContextValue["yScales"],
    data,
    lines,
    margin,
    xAccessor,
    bisectX,
    canInteract,
    mode: interactionMode,
  });

  const referenceAreas = useMemo(
    () => extractReferenceAreaConfigs(children),
    [children]
  );

  useEffect(() => {
    if (!hasProjection) {
      return;
    }
    resetPointState();
  }, [hasProjection, interactionMode, resetPointState]);

  const isFloor = projectionLayout === "floor";
  const projectionLanes = isFloor ? 1 : Math.max(1, lines.length);
  const laneDepth =
    projectionDepth ??
    Math.max(
      1,
      Math.min(
        innerWidth * MAX_LANE_SHARE,
        (innerWidth * MAX_DEPTH_SHARE) / projectionLanes
      )
    );
  const pillarExtent = isFloor
    ? Math.max(
        PILLAR_EXTENT_MIN,
        Math.min(innerWidth, innerHeight) * PILLAR_EXTENT_SHARE
      )
    : 0;
  const pillarFootprint = isFloor
    ? Math.max(PILLAR_FOOTPRINT_MIN, innerWidth * PILLAR_FOOTPRINT_SHARE)
    : 0;

  const slotKeys = useMemo<number[]>(() => {
    if (!isFloor) {
      return Array.from({ length: projectionLanes }, (_, lane) => lane);
    }
    const bandOffset = bandCenterOffset(xScale);
    const position = xScale as (value: XRawValue) => number | undefined;
    const entries: { key: number; depth: number }[] = [];
    data.forEach((d, row) => {
      const cx = (position(xAccessor(d)) ?? 0) + bandOffset;
      lines.forEach((line, series) => {
        const value = d[line.dataKey];
        if (typeof value !== "number") {
          return;
        }
        const axisScale = yScales[normalizeYAxisId(line.yAxisId)] ?? yScale;
        const cy = axisScale(value) ?? 0;
        entries.push({
          key: scatterFloorSlot(row, series, lines.length),
          depth: depthOf(FLOOR_ISO_CAMERA, cx, cy),
        });
      });
    });
    return entries
      .sort((a, b) => a.depth - b.depth || a.key - b.key)
      .map((entry) => entry.key);
  }, [isFloor, projectionLanes, data, lines, xScale, xAccessor, yScales, yScale]);
  const slotSignature = slotKeys.join(",");

  const slotRootRef = useRef<SVGGElement | null>(null);
  const [slots, setSlots] = useState<ReadonlyMap<number, SVGGElement>>(
    () => new Map()
  );

  useLayoutEffect(() => {
    if (!hasProjection) {
      return;
    }
    const root = slotRootRef.current;
    const next = new Map<number, SVGGElement>();
    if (root) {
      for (const child of Array.from(root.children)) {
        const lane = child.getAttribute("data-lane");
        if (lane !== null) {
          next.set(Number(lane), child as SVGGElement);
        }
      }
    }
    setSlots(next);
  }, [hasProjection, slotSignature]);

  const tapPoint = useCallback(
    (tooltip: TooltipData, key: string) =>
      pointTap(tooltip, key, slotRootRef.current),
    [pointTap]
  );

  const pointInteraction = interactionMode === "point";

  const scatterProjection = useMemo<ScatterProjectionValue | null>(
    () =>
      hasProjection
        ? {
            slots,
            lanes: projectionLanes,
            laneDepth,
            layout: projectionLayout,
            pillarExtent,
            pillarFootprint,
            pointInteraction,
            interactive: canInteract && pointInteraction,
            enter: pointEnter,
            leave: pointLeave,
            tap: tapPoint,
          }
        : null,
    [
      hasProjection,
      slots,
      projectionLanes,
      laneDepth,
      projectionLayout,
      pillarExtent,
      pillarFootprint,
      pointInteraction,
      canInteract,
      pointEnter,
      pointLeave,
      tapPoint,
    ]
  );

  if (width < 10 || height < 10) {
    return null;
  }

  const defsChildren: ReactElement[] = [];
  const clipExcludedChildren: ReactElement[] = [];
  const underlayChildren: ReactElement[] = [];
  const preOverlayChildren: ReactElement[] = [];
  const postOverlayChildren: ReactElement[] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      return;
    }

    if (isGradientDefComponent(child)) {
      defsChildren.push(child);
    } else if (isPatternDefComponent(child)) {
      preOverlayChildren.push(child);
    } else if (isPostOverlayComponent(child)) {
      postOverlayChildren.push(child);
    } else if (isClipExcludedComponent(child)) {
      clipExcludedChildren.push(child);
    } else if (isUnderlayComponent(child)) {
      underlayChildren.push(child);
    } else {
      preOverlayChildren.push(child);
    }
  });

  const contextValue: ChartContextValue = {
    ...DEFAULT_CHART_LIFECYCLE,
    data,
    renderData: data,
    xScale,
    xScaleKind,
    yScale: yScale as ChartContextValue["yScale"],
    yScales: yScales as ChartContextValue["yScales"],
    width,
    height,
    innerWidth,
    innerHeight,
    margin,
    columnWidth,
    tooltipData,
    setTooltipData,
    containerRef,
    lines,
    referenceAreas,
    isLoaded,
    animationDuration,
    animationEasing,
    enterTransition,
    revealEpoch,
    xAccessor,
    dateLabels,
    selection,
    clearSelection,
  };

  const chartSvg = (
    <svg
      aria-hidden="true"
      className="overflow-visible"
      height={height}
      width={width}
    >
      {defsChildren.length > 0 && <defs>{defsChildren}</defs>}

      <rect fill="transparent" height={height} width={width} x={0} y={0} />

      <g
        {...interactionHandlers}
        style={interactionStyle}
        transform={`translate(${margin.left},${margin.top})`}
      >
        <rect
          fill="transparent"
          height={innerHeight}
          width={innerWidth}
          x={0}
          y={0}
        />

        {clipExcludedChildren}
        {hasProjection ? (
          <ProjectionFadeOut>{underlayChildren}</ProjectionFadeOut>
        ) : (
          underlayChildren
        )}
        {/* Held: the points stay unmounted so their enter animation starts
            on the commit that releases the gate. Grid and axes keep
            rendering, as they do outside the time-series series clip. */}
        {holdReveal ? null : preOverlayChildren}
        {hasProjection && (
          <g ref={slotRootRef}>
            {slotKeys.map((lane) => (
              <g data-lane={lane} key={lane} />
            ))}
          </g>
        )}
        {postOverlayChildren}
      </g>
    </svg>
  );

  return (
    <ChartProvider value={contextValue}>
      {projection !== undefined ? (
        <ChartProjectionProvider
          depth={isFloor ? innerHeight : projectionLanes * laneDepth}
          height={innerHeight}
          kind={isFloor ? "quadrant" : "scatter"}
          mode={projection}
          onSettledChange={setProjectionSettled}
          pad={PROJECTION_PAD}
          width={innerWidth}
          zExtent={isFloor ? pillarExtent : innerHeight}
        >
          <ScatterProjectionProvider value={scatterProjection}>
            {chartSvg}
          </ScatterProjectionProvider>
        </ChartProjectionProvider>
      ) : (
        chartSvg
      )}
    </ChartProvider>
  );
}
